import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

const TONE_COLORS = {
    red: "#e26f67",
    amber: "#cda834",
    green: "#429574",
    grey: "#94a3b8",
};

export function funnelChartHeight(rowCount) {
    return Math.max(360, Number(rowCount || 0) * 42 + 56);
}

function StatusTick({ x, y, payload }) {
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={-10} y={0} dy={4} textAnchor="end" fill="#475569" fontSize={12} fontWeight={500}>
                {payload?.value}
            </text>
        </g>
    );
}

function FunnelTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    return (
        <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3 shadow-xl">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{row?.stage}</p>
            <p className="mt-1 max-w-xs text-sm font-semibold text-slate-900">{row?.name}</p>
            <p className="mt-1 text-sm text-slate-600">
                Кандидатов: <span className="font-bold tabular-nums text-slate-900">{row?.count || 0}</span>
            </p>
        </div>
    );
}

export default function FunnelChart({ data }) {
    if (!data || data.length === 0) {
        return (
            <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-bold text-slate-900">Воронка найма</h2>
                <p className="mt-2 text-sm text-slate-500">Нет данных для отображения</p>
            </section>
        );
    }

    const height = funnelChartHeight(data.length);

    return (
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm" data-testid="funnel-chart">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-6 py-5">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-700">Распределение</p>
                    <h2 className="mt-1 text-xl font-bold text-slate-900">Воронка найма</h2>
                    <p className="mt-1 text-sm text-slate-500">
                        Горизонтальная шкала сохраняет читаемость при любом количестве статусов.
                    </p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">
                    {data.length} статусов
                </span>
            </div>

            <div
                className="max-h-[680px] overflow-auto px-3 pb-4 pt-3 sm:px-5"
                data-testid="funnel-chart-scroll"
                aria-label="Диаграмма распределения кандидатов по статусам"
            >
                <div className="min-w-[680px]" style={{ height }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={data}
                            layout="vertical"
                            margin={{ top: 8, right: 54, left: 8, bottom: 24 }}
                            barCategoryGap="24%"
                        >
                            <CartesianGrid horizontal={false} stroke="#e2e8f0" strokeDasharray="3 4" />
                            <XAxis
                                type="number"
                                allowDecimals={false}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: "#94a3b8", fontSize: 11 }}
                            />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={218}
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                                tick={<StatusTick />}
                            />
                            <Tooltip cursor={{ fill: "#f8fafc" }} content={<FunnelTooltip />} />
                            <Bar dataKey="count" name="Кандидаты" radius={[0, 7, 7, 0]}>
                                {data.map((row, index) => (
                                    <Cell key={`${row.value || row.name}-${index}`} fill={TONE_COLORS[row.tone] || TONE_COLORS.grey} />
                                ))}
                                <LabelList dataKey="count" position="right" fill="#334155" fontSize={12} fontWeight={700} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </section>
    );
}
